import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { NavLink } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

import { Search, Button, InlineLoading } from "@wfp/ui";
import {
  faChalkboardTeacher,
  faArrowLeft,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  getCompactSchools,
  fetchSchoolsPaginated,
  clearCurrentSchoolState,
} from "data-handler/ducks/schools";
import {
  getCurrentUser,
  getIsSchoolStaff,
  getLoggedInOffline,
  getRawDataExtractionPermission,
  getReportApproverAccess,
} from "data-handler/ducks/auth";
import { getFeatureFlag } from "data-handler/ducks/featureFlags";
import Empty from "components/Empty";
import ModalExtended from "components/ModalExtended";
import Pagination from "components/Pagination";
import { RepeaterItem } from "components/Repeater";
import OnlineOffline from "components/OnlineOffline";
import RawDataExtractModal from "./rawDataExtractModal";
import styles from "./styles.module.scss";
import {
  getCanUseOfflineMode,
  getIsAdmin,
  getIsCountryAdmin,
  getIsInternal,
} from "helpers/users";

import { getDashboardLinks } from "data-handler/ducks/links";
import { AxiosResponse } from "axios";
import { getUserStoresProgress } from "data-handler/ducks/stores";
import queryString from "query-string";

type SchoolOverviewDetails = {
  is_current_school_year: boolean;
  days_since_last_sync: number;
  number_of_submitted_reports: number;
};

type CompactSchool = {
  id: number;
  name: string;
  adm_1: { name: string };
  adm_4: { name: string };
  country: string | { id?: number | string; name?: string };
  country_id?: number | string;
  overview_details?: SchoolOverviewDetails;
};

type SchoolSelectProps = {};

/**
 * Modal containing links which redirect to other schools.
 *
 * Only useful for admin users.
 */
const SchoolSelect: React.ElementType<SchoolSelectProps> = () => {
  const intl = useIntl();
  const [fetching, setFetching] = useState<boolean>(false);
  const [schools, setSchools] = useState<CompactSchool[]>([]);
  const [schoolsCount, setSchoolsCount] = useState(0);

  const [schoolFilter, setSchoolFilter] = useState("");
  const [filterEntry, setFilterEntry] = useState("");
  const [rawDataExtractModalState, setRawDataExtractModalState] = useState(
    false
  );
  const dispatch = useDispatch();

  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const history = useHistory();
  const location = useLocation();
  const currentUser = useSelector(getCurrentUser);
  const isSchoolStaff = useSelector(getIsSchoolStaff);
  const isOffline = useSelector(getLoggedInOffline);
  const hasRawDataExtractionPermission = useSelector(
    getRawDataExtractionPermission
  );
  const hasReportApproverAccess = useSelector(getReportApproverAccess);
  const isAnalyticsViewEnabled = useSelector(
    getFeatureFlag("ENABLE_ANALYTICS_VIEW")
  );
  const dashboardLinks = useSelector(getDashboardLinks);
  const isInternal = getIsInternal(currentUser?.email);
  const canUseOfflineMode = getCanUseOfflineMode(currentUser);
  const userStoresProgress = useSelector(getUserStoresProgress);

  const canViewAnalytics =
    isAnalyticsViewEnabled &&
    (getIsAdmin(currentUser) || hasRawDataExtractionPermission);
  const compactSchools = useSelector(getCompactSchools);

  const getSchoolCountryId = (school: CompactSchool): string | undefined => {
    const schoolWithAnyFields = school as any;
    const matchedCompactSchool = (compactSchools || []).find(
      (compactSchool: any) => compactSchool.id === school.id
    ) as any;

    const rawCountryId =
      schoolWithAnyFields.country_id ??
      schoolWithAnyFields.country?.id ??
      matchedCompactSchool?.country_id ??
      matchedCompactSchool?.country?.id;

    if (
      rawCountryId === null ||
      rawCountryId === undefined ||
      rawCountryId === ""
    ) {
      return undefined;
    }

    return String(rawCountryId);
  };

  const analyticsLink = (school: CompactSchool) => {
    const params = queryString.parse(location.search);
    const schoolCountryId = getSchoolCountryId(school);
    delete params.select;
    delete params.country_id;

    return {
      pathname: "/analytics/Dashboard",
      search: queryString.stringify({
        ...params,
        ...(schoolCountryId ? { country_id: schoolCountryId } : {}),
        school_id: school.id,
      }),
    };
  };

  // We do not need this because we watch for paginated school request to complete.
  // and keep track of fetching status manually in variable 'fetching'
  //const isCompactSchoolsFetching = useSelector(getCompactSchoolsFetching);
  //const compactSchoolsCount = useSelector(getCompactSchoolsCount);

  const country = currentUser.country;
  const hasAdminAccess =
    (getIsCountryAdmin(currentUser) && country) || getIsAdmin(currentUser);

  const closeWindow = () => {
    const params = new URLSearchParams(history.location.search);
    params.delete("select");
    history.replace({
      pathname: history.location.pathname,
      search: params.toString() ? `?${params.toString()}` : "",
    });
  };

  // if we are already fetching something, do not update filter.
  useEffect(() => {
    if (!fetching) {
      setSchoolFilter(filterEntry);
    }
  }, [fetching, filterEntry]);

  type FetchUserPaginatedResult = {
    results: CompactSchool[];
    count: number;
  };

  useEffect(() => {
    if (canUseOfflineMode) return;
    setFetching(true);
    const offset = (pageNumber - 1) * pageSize;
    const limit = pageSize;
    const emptySchools = [];
    // Prefill the Modal with empty items
    // Keeps the Modal at a standard height.
    // Stops it from jumping around.
    for (let i = 0; i < 20; i++) {
      emptySchools.push({
        id: i,
        name: "",
        adm_1: { name: "" },
        adm_4: { name: "" },
        country: "",
      });
    }
    setSchools(emptySchools);

    fetchSchoolsPaginated(schoolFilter, limit, offset, false)
      .then((res: AxiosResponse<FetchUserPaginatedResult>) => {
        const { results, count } = res.data;
        setSchools(results);
        setSchoolsCount(count);
        setFetching(false);
        return res;
      })
      .catch(() => {
        setFetching(false);
      });
  }, [canUseOfflineMode, pageNumber, pageSize, schoolFilter]);

  useEffect(() => {
    let filteredSchools: CompactSchool[] = [];
    if (!fetching) {
      if (canUseOfflineMode) {
        const lowerSchoolFilter = schoolFilter.toLocaleLowerCase();
        // eslint-disable-next-line react-hooks/exhaustive-deps
        filteredSchools = compactSchools.filter((school: CompactSchool) =>
          school.name.toLocaleLowerCase().includes(lowerSchoolFilter)
        );

        setSchoolsCount(filteredSchools.length);
        const start = (pageNumber - 1) * pageSize;
        const end = start + pageSize;
        filteredSchools = filteredSchools.slice(start, end);
        setSchools(filteredSchools);
      }
    }
  }, [
    fetching,
    pageNumber,
    pageSize,
    schoolFilter,
    compactSchools,
    canUseOfflineMode,
  ]);

  const onChange = ({ page, pageSize }: { page: number; pageSize: number }) => {
    setPageNumber(page);
    setPageSize(pageSize);
  };

  const paginationProps = {
    pageSizes: [10, 20, 30, 40, 50],
    page: pageNumber,
    pageSize: pageSize,
    totalItems: schoolsCount,
    onChange,
  };

  const schoolLinkText = (school: CompactSchool) => {
    const schoolCountryName =
      typeof school.country === "string"
        ? school.country
        : school.country?.name || "";

    if (fetching) {
      return schoolCountryName;
    } else {
      return (
        schoolCountryName +
        (school.adm_1.name ? " / " + school.adm_1.name : "") +
        (school.adm_4.name ? " / " + school.adm_4.name : "")
      );
    }
  };

  const onChangeFilter = (searchText: string = "") => {
    setFilterEntry(searchText.toLowerCase());
  };

  const dashboardLink = dashboardLinks?.find(
    (item: any) => item.internal === isInternal
  )?.url;
  const retrievingStoresLabel = intl.formatMessage({
    id: "schoolSelect.retrievingSchools",
    defaultMessage: "Retrieving schools data...",
  });
  const retrievingDescription = retrievingStoresLabel;
  const header = (
    <div className={styles.header}>
      {schoolsCount} {/* Space between number of schools and title */}
      {schoolsCount !== 1 && (
        <FormattedMessage
          id="schoolSelect.schoolsTitle"
          defaultMessage="Schools"
        />
      )}
      {schoolsCount === 1 && (
        <FormattedMessage
          id="schoolSelect.schoolTitle"
          defaultMessage="School"
        />
      )}
      <div className={styles.buttonContainer}>
        <div className={styles.buttonRow}>
          {!isOffline &&
            (hasRawDataExtractionPermission || getIsAdmin(currentUser)) && (
              <>
                <Button
                  onClick={() => {
                    if (isAnalyticsViewEnabled) {
                      const params = queryString.parse(location.search);
                      delete params.select;
                      history.push({
                        pathname: "/analytics/DataDownload",
                        search: queryString.stringify(params),
                      });
                    } else {
                      setRawDataExtractModalState(true);
                    }
                  }}
                  className={styles.dataButton}
                >
                  <FormattedMessage
                    id="CountryAdmin.RawDataExtractModal"
                    defaultMessage="Raw Data Extract"
                  />
                </Button>
                {isAnalyticsViewEnabled && (
                  <NavLink to={"/analytics/"}>
                    <Button>
                      <FormattedMessage
                        id="schoolSelect.analytics"
                        defaultMessage="Analytics"
                      />
                    </Button>
                  </NavLink>
                )}
              </>
            )}
          {!isOffline && !isSchoolStaff && (
            <a target="_blank" href={dashboardLink} rel="noopener noreferrer">
              <Button>
                <FormattedMessage
                  id="CountryAdmin.dashboardLinkLabel"
                  defaultMessage="Dashboard"
                />
              </Button>
            </a>
          )}
          {!isOffline && hasAdminAccess && (
            <>
              <OnlineOffline
                onlineChildren={
                  <NavLink to={"/countryAdmin/"}>
                    <Button>
                      <FormattedMessage
                        id="CountryAdmin.countryAdmin"
                        defaultMessage="Country Admin"
                      />
                    </Button>
                  </NavLink>
                }
                offlineChildren={
                  <Button
                    className={styles.offlineAdminBtn}
                    small
                    kind="accent"
                  >
                    <FormattedMessage
                      id="CountryAdmin.adminPortalNotAvailable"
                      defaultMessage="Offline, admin portal not available"
                    />
                  </Button>
                }
              />
            </>
          )}

          {/* /approver/ is a separate app served from the same origin,
              so it needs a full page navigation, not a NavLink */}
          {!isOffline && (getIsAdmin(currentUser) || hasReportApproverAccess) && (
            <a href="/approver/">
              <Button>
                <FormattedMessage
                  id="schoolSelect.approver"
                  defaultMessage="Approver"
                />
              </Button>
            </a>
          )}

          <NavLink exact to={`/logout`}>
            <Button
              className={styles.offlineAdminBtn}
              icon={<FontAwesomeIcon icon={faArrowLeft} />}
            >
              <FormattedMessage
                id="UserNavigation.logoutButtonLabel"
                defaultMessage="Logout"
              />
            </Button>
          </NavLink>
        </div>
        {userStoresProgress?.active && (
          <div
            className={styles.progressIndicator}
            data-testid="schools-progress"
          >
            <InlineLoading description={retrievingDescription} />
            {userStoresProgress.total ? (
              <span className={styles.progressCounts}>
                {`(${userStoresProgress.completed}/${userStoresProgress.total})`}
              </span>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );

  const schoolInfoRow = (school: CompactSchool) => {
    return (
      <div className={styles.schoolInfoRow}>
        <div className={styles.schoolInfoRowSchoolName}>{school.name}</div>
        <div style={{ fontSize: "small", textDecoration: "none" }}>
          <FormattedMessage
            id="schoolSelect.daysSinceLastSync"
            defaultMessage="{days_since_last_sync} days since last sync"
            values={{
              days_since_last_sync:
                school.overview_details?.days_since_last_sync,
            }}
          />
        </div>
        <div style={{ fontSize: "small" }}>
          <FormattedMessage
            id="schoolSelect.numberSubmittedReports"
            defaultMessage="{number_of_submitted_reports} reports available for approval"
            values={{
              number_of_submitted_reports:
                school.overview_details?.number_of_submitted_reports,
            }}
          />
        </div>
      </div>
    );
  };

  const onSchoolRowClick = (schoolId: number) => {
    dispatch(clearCurrentSchoolState());
    history.push(`/school/${schoolId}`);
  };

  if (rawDataExtractModalState) {
    return (
      <RawDataExtractModal
        state={rawDataExtractModalState}
        setState={setRawDataExtractModalState}
      />
    );
  } else {
    return (
      <ModalExtended
        open
        onRequestClose={closeWindow}
        modalLabel={
          <FormattedMessage
            id="schoolSelect.schoolOverview"
            defaultMessage="School overview"
          />
        }
        modalHeading={header}
        wide
        passiveModal
        selectorPrimaryFocus={false}
        secondaryButtonText={""}
      >
        <div className={styles.schoolSelect}>
          <div className={styles.search}>
            <Search
              kind="large"
              name="input-name"
              labelText={
                <FormattedMessage
                  id="schoolSelect.filterSchools"
                  defaultMessage="Filter schools"
                />
              }
              onChange={onChangeFilter}
            />
          </div>

          {(!schools || schools.length === 0) && fetching ? (
            <Empty
              title="Loading schools"
              icon={<FontAwesomeIcon icon={faChalkboardTeacher} size="1x" />}
            >
              Wait for a moment
            </Empty>
          ) : !schools || schools.length === 0 ? (
            <Empty
              title={
                <FormattedMessage
                  id="Common.noSchoolFound"
                  defaultMessage="No school found"
                />
              }
              icon={<FontAwesomeIcon icon={faChalkboardTeacher} size="1x" />}
            >
              {currentUser.is_approver ? (
                <FormattedMessage
                  id="Approver.noSchoolsMessage"
                  defaultMessage="Dear {other_names} {last_name} you have no schools assigned, please contact your Country Administrator for assignment of schools"
                  values={{
                    other_names: currentUser.other_names,
                    last_name: currentUser.last_name,
                  }}
                />
              ) : (
                <FormattedMessage
                  id="Common.changeFilter"
                  defaultMessage="Change the filter"
                />
              )}
            </Empty>
          ) : (
            <>
              {schools.map((school) => (
                <RepeaterItem key={school.id}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <NavLink
                      onClick={() => onSchoolRowClick(school.id)}
                      to={`/school/${school.id}`}
                      style={{ flex: 1 }}
                    >
                      {school?.overview_details?.is_current_school_year ? (
                        schoolInfoRow(school)
                      ) : (
                        <div>{school.name}</div>
                      )}
                      <div className={styles.country}>
                        {schoolLinkText(school)}
                      </div>
                    </NavLink>
                    {canViewAnalytics && (
                      <NavLink
                        to={analyticsLink(school)}
                        title={intl.formatMessage({
                          id: "schoolSelect.analytics",
                          defaultMessage: "Analytics",
                        })}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#0062c7",
                          fontSize: "16px",
                          textDecoration: "none",
                          marginLeft: "10px",
                          padding: "8px",
                        }}
                      >
                        <FontAwesomeIcon icon={faChartLine} />
                      </NavLink>
                    )}
                  </div>
                </RepeaterItem>
              ))}
            </>
          )}
          <div>
            <Pagination {...paginationProps} />
          </div>
        </div>
      </ModalExtended>
    );
  }
};

const InitializedSchoolSelect: React.ComponentType<SchoolSelectProps> = (
  props
) => {
  return <SchoolSelect {...props} />;
};

export default InitializedSchoolSelect;
